import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import theme from 'shared/styles/theme';

const CRUNCHING_NUMBERS_AFTER_MS = 5000;
const CHECKING_DETAILS_AFTER_MS = 10000;

type LoadingStep =
  'settingUp' | 'crunchingNumbers' | 'checkingDetails' | 'longWait';

interface ScheduledStep {
  step: LoadingStep;
  afterMs: number;
}

const TIMED_STEPS: ScheduledStep[] = [
  { step: 'crunchingNumbers', afterMs: CRUNCHING_NUMBERS_AFTER_MS },
  { step: 'checkingDetails', afterMs: CHECKING_DETAILS_AFTER_MS },
];

const STEP_TITLE_KEYS: Record<LoadingStep, string> = {
  settingUp: 'settingUp',
  crunchingNumbers: 'crunchingNumbers',
  checkingDetails: 'checkingDetails',
  longWait: 'longWait.title',
};

type LoadingSequenceProps =
  | { longWaitAfterMs?: never; onFinishInBackground?: never }
  | { longWaitAfterMs: number; onFinishInBackground: () => void };

const LoadingSequence = ({
  longWaitAfterMs,
  onFinishInBackground,
}: LoadingSequenceProps) => {
  const { t: translatedText } = useTranslation('payLater', {
    keyPrefix: 'loadingSequence',
  });
  const { bottom } = useSafeAreaInsets();
  const [currentStep, setCurrentStep] = useState<LoadingStep>('settingUp');

  useEffect(() => {
    const scheduledSteps: ScheduledStep[] =
      longWaitAfterMs === undefined
        ? TIMED_STEPS
        : [...TIMED_STEPS, { step: 'longWait', afterMs: longWaitAfterMs }];

    const timeouts = scheduledSteps.map(({ step, afterMs }) =>
      setTimeout(() => {
        setCurrentStep(step);
        AccessibilityInfo.announceForAccessibility(
          translatedText(STEP_TITLE_KEYS[step]),
        );
      }, afterMs),
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [longWaitAfterMs, translatedText]);

  const isLongWait = currentStep === 'longWait';
  const bodyText = isLongWait
    ? translatedText('longWait.body')
    : translatedText('body');
  const footerBottomPadding = bottom + theme.spacing.m;

  return (
    <View style={styles.container}>
      <View style={styles.loadingContainer}>
        <FullScreenLoading
          isCoinLoader
          titleText={translatedText(STEP_TITLE_KEYS[currentStep])}
          bodyText={bodyText}
        />
      </View>
      {isLongWait && onFinishInBackground ? (
        <View style={[styles.footer, { paddingBottom: footerBottomPadding }]}>
          <Button
            label={translatedText('longWait.finishInBackground')}
            onPress={onFinishInBackground}
            type="SECONDARY"
            width="FULL"
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  footer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  loadingContainer: { flex: 1 },
});

export default LoadingSequence;
